/**
 * Skills Data
 * Contains technical and soft skills with proficiency levels
 */

import React from 'react';
import { 
  FaCode, FaJava, FaTree, FaCogs, FaUsers, FaLightbulb, FaComments, 
  FaClock, FaSyncAlt, FaBrain, FaPalette 
} from 'react-icons/fa';
import {
  SiCplusplus, SiCsharp, SiXamarin, SiPython, SiPytorch, SiNumpy, SiPandas, 
  SiScikitlearn, SiJupyter, SiRobotframework, SiAppium, SiAndroidstudio, 
  SiGit, SiAzuredevops, SiJenkins, SiDocker, SiMqtt, SiHtml5, SiJavascript, 
  SiPhp, SiUbuntu, SiMysql, SiMongodb, SiApachehadoop, SiApachespark, 
  SiApachemaven, SiWireshark, SiReact, SiDotnet, SiJson, SiPowershell, 
  SiGnubash, SiAnsible, SiAmazonaws, SiKubernetes, SiSelenium, 
  SiPostman, SiOpengl, SiJira, SiPostgresql, SiAmazondynamodb, SiAmazon, 
  SiPowerbi, SiWindows, SiApple, SiLinux, SiKalilinux, SiCentos, 
  SiOpensuse, SiArchlinux
} from 'react-icons/si';
import { VscTerminal } from 'react-icons/vsc';
import { 
  GiArtificialIntelligence, GiBrain 
} from 'react-icons/gi';
import { 
  AiOutlineApi, AiOutlineCloudServer 
} from 'react-icons/ai';
import { 
  BsShieldLock, BsGraphUp 
} from 'react-icons/bs';
import { 
  MdSecurity, MdOutlineSettings 
} from 'react-icons/md';
import { 
  RiUserStarLine 
} from 'react-icons/ri';
import type { SkillsData } from '@/types';

// MATLAB Icon Component
const MatlabIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    viewBox="0 0 512 512"
    className={className}
  >
    <path
      fill="currentColor"
      d="M495.855 367.604c-28.98-73.177-53.037-148.231-80.725-221.893c-12.23-31.362-24.198-62.986-40.868-92.33c-6.62-10.535-12.956-22.119-23.814-28.86c-2.739-1.74-5.828-2.479-8.88-2.42c-5.084.096-10.14 2.37-14.025 5.65c-14.571 11.543-23.209 28.334-32.896 43.793c-17.114 28.698-35.156 58.748-63.773 77.396c-13.44 9.485-31.039 10.514-43.995 20.686c-17.72 13.36-29.767 32.31-44.298 48.737c-3.31 3.956-8.436 5.53-12.916 7.67C86.315 243.71 42.945 261.35 0 279.916c36.367 28.132 75.115 53.157 112.208 80.321c10.172-2.018 20.383-6.196 30.877-4.339c16.63 5.207 26.377 21.15 34.006 35.721c15.5 31.765 26.7 65.307 39.253 98.283c20.988-1.493 39.878-12.31 56.104-25.025c31.24-25.307 55.034-58.001 81.23-88.192c12.552-13.199 25.347-28.92 44.197-32.593c19.05-4.601 39.454 2.22 53.581 15.338c19.818 17.719 37.376 38.203 60.544 51.765c-3.108-15.338-10.575-29.101-16.145-43.592M171.844 316.04c-18.284 10.817-37.699 19.717-56.71 29.162c-29.828-19.98-58.889-41.15-88.192-61.856c39.515-17.315 79.635-33.4 119.675-49.545c18.97 14.813 38.547 28.88 57.517 43.693c-8.92 14.248-19.657 27.487-32.29 38.546m40.665-49.646c-18.85-14.167-37.94-27.951-56.508-42.482c11.907-16.226 24.945-31.906 40.565-44.701c10.333-6.62 22.966-8.456 33.703-14.43c25.469-12.714 42.845-36.044 59.434-58.324c-21.271 55.216-43.975 110.654-77.194 159.937"
    />
  </svg>
);

export const skills: SkillsData = {
  technical: {
    "Languages & Frameworks": [
      { name: "C/C++", icon: SiCplusplus, level: 90 },
      { name: "C#", icon: SiCsharp, level: 85 },
      { name: "Python", icon: SiPython, level: 90 },
      { name: "Java", icon: FaJava, level: 80 },
      { name: "JavaScript", icon: SiJavascript, level: 80 },
      { name: "PHP", icon: SiPhp, level: 70 },
      { name: "PowerShell", icon: SiPowershell, level: 80 },
      { name: "Bash", icon: SiGnubash, level: 75 },
      { name: "Z shell", icon: VscTerminal, level: 70 },
      { name: "Xamarin", icon: SiXamarin, level: 80 },
      { name: "XAML", icon: FaCode, level: 85 },
      { name: "React", icon: SiReact, level: 80 },
      { name: "ASP.NET", icon: SiDotnet, level: 75 },
      { name: "Robot Framework", icon: SiRobotframework, level: 70 },
      { name: "Appium", icon: SiAppium, level: 70 },
      { name: "HTML/CSS", icon: SiHtml5, level: 85 },
      { name: "REST API", icon: AiOutlineApi, level: 85 },
      { name: "JSON", icon: SiJson, level: 85 }
    ],
    "Data Science & AI": [
      { name: "Machine Learning", icon: GiArtificialIntelligence, level: 85 },
      { name: "Deep Learning", icon: GiBrain, level: 80 },
      { name: "PyTorch", icon: SiPytorch, level: 75 },
      { name: "Scikit-learn", icon: SiScikitlearn, level: 80 },
      { name: "MATLAB", icon: MatlabIcon, level: 75 },
      { name: "NumPy", icon: SiNumpy, level: 85 },
      { name: "Pandas", icon: SiPandas, level: 85 },
      { name: "Matplotlib", icon: BsGraphUp, level: 80 },
      { name: "Jupyter", icon: SiJupyter, level: 90 },
      { name: "Hadoop", icon: SiApachehadoop, level: 70 },
      { name: "PySpark", icon: SiApachespark, level: 70 },
      { name: "Power BI", icon: SiPowerbi, level: 70 }
    ],
    "Cloud & DevOps": [
      { name: "AWS", icon: SiAmazonaws, level: 75 },
      { name: "Azure DevOps", icon: SiAzuredevops, level: 85 },
      { name: "Amazon S3", icon: SiAmazon, level: 75 },
      { name: "DynamoDB", icon: SiAmazondynamodb, level: 70 },
      { name: "Docker", icon: SiDocker, level: 75 },
      { name: "Kubernetes", icon: SiKubernetes, level: 70 },
      { name: "Ansible", icon: SiAnsible, level: 70 },
      { name: "Jenkins", icon: SiJenkins, level: 80 },
      { name: "Git", icon: SiGit, level: 90 },
      { name: "Selenium", icon: SiSelenium, level: 70 },
      { name: "Postman", icon: SiPostman, level: 85 },
      { name: "Jira", icon: SiJira, level: 75 },
      { name: "Maven", icon: SiApachemaven, level: 75 }
    ],
    "Databases & Security": [
      { name: "MySQL", icon: SiMysql, level: 85 },
      { name: "PostgreSQL", icon: SiPostgresql, level: 80 },
      { name: "MongoDB", icon: SiMongodb, level: 75 },
      { name: "Cybersecurity", icon: BsShieldLock, level: 75 },
      { name: "Network Security", icon: MdSecurity, level: 70 },
      { name: "Web Security", icon: BsShieldLock, level: 70 },
      { name: "Cryptography", icon: AiOutlineCloudServer, level: 65 },
      { name: "Wireshark", icon: SiWireshark, level: 70 }
    ],
    "Systems & Platforms": [
      { name: "Windows", icon: SiWindows, level: 90 },
      { name: "macOS", icon: SiApple, level: 85 },
      { name: "Linux", icon: SiLinux, level: 85 },
      { name: "Ubuntu", icon: SiUbuntu, level: 85 },
      { name: "Kali", icon: SiKalilinux, level: 70 },
      { name: "CentOS", icon: SiCentos, level: 70 },
      { name: "OpenSUSE", icon: SiOpensuse, level: 65 },
      { name: "Arch Linux", icon: SiArchlinux, level: 65 },
      { name: "Pintos", icon: MdOutlineSettings, level: 70 },
      { name: "Android Studio", icon: SiAndroidstudio, level: 75 },
      { name: "GDB Debugger", icon: FaCogs, level: 70 },
      { name: "OpenCL", icon: SiOpengl, level: 65 },
      { name: "Data Structures & Algorithms", icon: FaTree, level: 90 },
      { name: "Operating Systems", icon: MdOutlineSettings, level: 80 },
      { name: "MQTT", icon: SiMqtt, level: 70 }
    ]
  },
  soft: [
    { name: "Leadership", icon: RiUserStarLine, level: 85 },
    { name: "Teamwork", icon: FaUsers, level: 90 },
    { name: "Problem Solving", icon: FaLightbulb, level: 90 },
    { name: "Communication", icon: FaComments, level: 85 },
    { name: "Time Management", icon: FaClock, level: 85 },
    { name: "Adaptability", icon: FaSyncAlt, level: 90 },
    { name: "Critical Thinking", icon: FaBrain, level: 85 },
    { name: "Creativity", icon: FaPalette, level: 80 }
  ]
};

